import { createTheme } from '@mui/material/styles';
import { CaretDownIcon, XCircle } from '@phosphor-icons/react';

import { themeColors } from '../consts/colors';

import type {} from '@mui/x-date-pickers/themeAugmentation';

import { dimensions } from '@fjuel/consts/dimensions';

export const theme = createTheme({
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          position: 'fixed',
          backgroundColor: themeColors.background.navy,
          height: dimensions.appBarHeight,
          zIndex: 100,
          color: 'inherit',
          boxShadow: 'none',
          borderBottom: `1px solid ${themeColors.border.navy}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: themeColors.background.navyLight,
          border: `1px solid ${themeColors.border.navy}`,
          borderRadius: '8px',
          boxShadow: themeColors.boxShadow.primary,
          padding: theme.spacing(0, 0.75, 0, 0.75),
        }),
        list: ({ theme }) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(0.5),
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: themeColors.text.light,
          borderRadius: '6px',
          fontSize: '14px',
          '&.Mui-selected': {
            fontWeight: 600,
            backgroundColor: 'rgba(139, 252, 182, 0.12)',
            color: themeColors.primary,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: themeColors.border.navy,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          padding: 0,
          margin: 0,
        },
        body: {
          backgroundColor: themeColors.background.navyDark,
          padding: 0,
          margin: 0,
          overflowX: 'hidden',
          scrollbarColor: 'inherit',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: 'inherit',
            width: '.8ex',
            height: '.8ex',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            backgroundColor: 'currentColor',
            minHeight: 24,
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#959595',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#959595',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#959595',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          margin: 0,
          '& .MuiInputBase-input.Mui-disabled, & .MuiInputLabel-root.Mui-disabled': {
            WebkitTextFillColor: 'hsla(0, 0%, 100%, 0.4)',
            cursor: 'not-allowed',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: themeColors.background.navyLight,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 4,
          marginBottom: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          transition: 'box-shadow 0.15s ease-in-out',
          '& .MuiOutlinedInput-notchedOutline': {
            transition: themeColors.transition[150],
            borderColor: themeColors.border.navy,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border.green },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.border.green,
            borderWidth: 1,
          },
          '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(139, 252, 182, 0.5)',
          },
        },
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: themeColors.background.navyLight,
            WebkitTextFillColor: themeColors.text.light,
          },
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: themeColors.background.navyLight,
          '& .MuiInputAdornment-root, & .MuiIconButton-root, & .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          transition: 'box-shadow 0.15s ease-in-out',
          '& .MuiPickersOutlinedInput-notchedOutline': {
            transition: themeColors.transition[150],
            borderColor: themeColors.border.navy,
          },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': { borderColor: themeColors.border.green },
          '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: themeColors.border.green,
            borderWidth: 1,
          },
          '&.Mui-disabled:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: 'rgba(139, 252, 182, 0.5)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 10,
          [theme.breakpoints.up('sm')]: {
            borderRadius: 14,
          },
          border: `1px solid ${themeColors.border.navy}`,
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          paddingTop: 16,
          paddingBottom: 16,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          marginTop: 16,
          marginBottom: 16,
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 24,
          gap: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          color: themeColors.background.navy,
        },
        text: {
          textTransform: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          color: themeColors.background.navy,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // backgroundColor: themeColors.background.navyLight,
          color: themeColors.text.light,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: CaretDownIcon,
      },
      styleOverrides: {
        icon: {
          color: themeColors.text.light,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: themeColors.icon.xs,
          transition: themeColors.transition[150],
        },
        iconOpen: {
          transform: 'translateY(-50%) rotate(180deg)',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <CaretDownIcon size={themeColors.icon.xs} />,
      },
      styleOverrides: {
        popupIndicator: {
          color: themeColors.text.light,
          transition: themeColors.transition[150],
        },
        popupIndicatorOpen: {
          transform: 'rotate(180deg)',
        },
        paper: ({ theme }) => ({
          marginTop: theme.spacing(1),
          backgroundColor: themeColors.background.navyLight,
          border: `1px solid ${themeColors.border.navy}`,
          borderRadius: '8px',
          boxShadow: themeColors.boxShadow.primary,
          padding: theme.spacing(0, 0.75),
        }),
        listbox: ({ theme }) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(0.5),
          padding: theme.spacing(1, 0),
          '& .MuiAutocomplete-option': {
            boxSizing: 'border-box',
            color: themeColors.text.light,
            borderRadius: '6px',
            fontSize: 14,
            lineHeight: 1.5,
            padding: theme.spacing(1, 2),
            '&:hover, &.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&[aria-selected="true"]': {
              fontWeight: 600,
              backgroundColor: 'rgba(139, 252, 182, 0.12)',
              color: themeColors.primary,
            },
            '&[aria-selected="true"].Mui-focused': {
              backgroundColor: 'rgba(139, 252, 182, 0.12)',
            },
            alignItems: 'flex-start',
            height: 'auto',
            whiteSpace: 'normal',
            overflow: 'visible',
          },
        }),
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          error: <XCircle />,
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#8BFCB6',
      contrastText: themeColors.text.light,
    },
    divider: themeColors.border.navy,
    secondary: {
      main: 'hsla(333, 75%, 60%, 1)',
      contrastText: 'hsla(333, 75%, 40%, 1)',
    },
    success: {
      main: '#8BFCB6',
      contrastText: '#2a4c37',
    },
    warning: {
      main: '#C9A800',
    },
    error: {
      main: '#e54c91',
    },
    background: {
      paper: themeColors.background.navyLight,
    },
    text: {
      primary: themeColors.text.light,
      secondary: themeColors.text.dark,
      disabled: 'hsla(0, 0%, 100%, 0.4)',
    },
    action: {
      disabled: 'hsla(0, 0%, 100%, 0.4)',
      disabledBackground: 'hsla(0, 0%, 100%, 0.52)',
      hover: 'hsla(0, 0%, 100%, 0.20)',
      focus: 'hsla(0, 0%, 100%, 0.12)',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 980,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: ['Inter', 'sans-serif'].join(','),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
});
